import { FLUSS_NODE_TYPES } from "modules/fluss/utils/bpmn";

type PopupEntries = Record<string, any>;
let scriptAuthorAllowed = true;
export const setFlussPopupScriptPermission = (allowed: boolean) => { scriptAuthorAllowed = allowed; };

// The third-party popup hides these executable Primum types with rank -1.
// Keep this list tied to FLUSS_NODE_TYPES, not to the whole BPMN catalog.
export const FLUSS_POPUP_UNHIDDEN_ENTRY_IDS = [
    "inclusive-gateway", // FLUSS_NODE_TYPES.inclusiveGateway
    "send-task", // FLUSS_NODE_TYPES.sendTask
    "manual-task", // FLUSS_NODE_TYPES.manualTask
] as const;

const FLUSS_POPUP_UNHIDDEN_TYPES = new Set<string>([
    FLUSS_NODE_TYPES.inclusiveGateway,
    FLUSS_NODE_TYPES.sendTask,
    FLUSS_NODE_TYPES.manualTask,
]);

const unsupportedEntry = (id: string) => [
    "complex-gateway", "receive-task", "none-intermediate-throw", "none-intermediate-throwing",
    "message-end", "escalation-end", "compensation-end",
].some((name) => id === name || id.endsWith(`-${name}`));

const unaccented = (value: string) => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const searchableEntry = (id: string, entry: any) => {
    const label = String(entry.label || "");
    const search = String(entry.search || "");
    const synonyms = id.includes("escalation") ? " escalação escalacao" : "";
    const extra = `${unaccented(label)} ${unaccented(search)}${synonyms}`.trim();
    return extra && (extra !== label || synonyms) ? { ...entry, search: `${search} ${extra}`.trim() } : entry;
};

const visibleEntries = (entries: PopupEntries) => Object.fromEntries(
    Object.entries(entries)
        .filter(([id]) => !unsupportedEntry(id) && (scriptAuthorAllowed || !id.includes("script-task")))
        .map(([id, entry]) => [id, searchableEntry(id, entry)])
);

// diagram-js merges providers in event order, so lower-than-default priority runs later and wins.
export const FLUSS_POPUP_OVERRIDE_PRIORITY = 500;

export const createPopupRankUpdater = (prefix: "create" | "append") => (entries: PopupEntries) => {
    let updated = visibleEntries(entries);

    FLUSS_POPUP_UNHIDDEN_ENTRY_IDS.forEach((actionName) => {
        const id = `${prefix}-${actionName}`;
        const entry = updated[id];
        if (!entry) return;

        updated = updated === entries ? { ...entries } : updated;
        updated[id] = { ...entry, rank: 0 };
    });

    return updated;
};

export function FlussPopupRankProvider(this: any, popupMenu: any) {
    this.getPopupMenuEntries = () => createPopupRankUpdater("create");
    popupMenu.registerProvider("bpmn-create", FLUSS_POPUP_OVERRIDE_PRIORITY, this);

    popupMenu.registerProvider("bpmn-append", FLUSS_POPUP_OVERRIDE_PRIORITY, {
        getPopupMenuEntries: () => createPopupRankUpdater("append"),
    });
    popupMenu.registerProvider("bpmn-replace", FLUSS_POPUP_OVERRIDE_PRIORITY, {
        getPopupMenuEntries: () => visibleEntries,
    });
}

(FlussPopupRankProvider as any).$inject = ["popupMenu"];

export const flussPopupRankModule = {
    __init__: ["flussPopupRankProvider"],
    flussPopupRankProvider: ["type", FlussPopupRankProvider],
};

export function FlussContextPadProvider(this: any, contextPad: any) {
    this.getContextPadEntries = () => (entries: PopupEntries) => {
        const visible = { ...entries };
        delete visible["append.intermediate-event"];
        return visible;
    };
    contextPad.registerProvider(FLUSS_POPUP_OVERRIDE_PRIORITY, this);
}

(FlussContextPadProvider as any).$inject = ["contextPad"];

export const flussContextPadModule = {
    __init__: ["flussContextPadProvider"],
    flussContextPadProvider: ["type", FlussContextPadProvider],
};

export const isFlussPopupUnhiddenType = (type: string) => FLUSS_POPUP_UNHIDDEN_TYPES.has(type);
